import './blogs.css'; // Import blog-specific CSS
import React, { useEffect } from "react";
import BlogTableOfContents from "../../components/BlogTableOfContents"; // Corrected path
import BlogCalloutBox from "../../components/BlogCalloutBox"; // Corrected path
import BlogTestimonial from "../../components/BlogTestimonial"; // Corrected path
import BlogStickySignup from "../../components/BlogStickySignup"; // Corrected path
import { SparkleButton } from '../../components/SparkleEffect'; // Corrected path
import SiteRouteHelmet from '../../components/SiteRouteHelmet';

const { ogImageUrlForPath } = require('../../site/ogTitleCard');
const { getPublicPageSeo } = require('../../site/publicPageSeo');
const { structuredDataForRoute } = require('../../site/structuredData');

const STATIC_BLOG_PATH = '/blog/using-ai-to-make-ai-profitable-swing-trading-bot-in-seconds';

export default function UsingAIToMakeAProfitableSwingTradingBotInSeconds() {
  useEffect(() => {
    // Simplify useEffect to just add IDs to headings
    const addIdsToHeadings = () => {
      const headings = document.querySelectorAll('.post-content h2, .post-content h3');
      headings.forEach(heading => {
        if (!heading.id) {
          heading.id = heading.textContent
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-');
        }
      });
    };
    
    // Run once on mount
    addIdsToHeadings();
    
    // Ensure normal scrolling behavior
    document.body.style.overflow = 'auto';
    document.documentElement.style.overflow = 'auto';
    
    return () => {
      // Clean up
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, []);

  const seo = getPublicPageSeo(STATIC_BLOG_PATH);
  const [article, ...otherStructuredData] = structuredDataForRoute(seo);
  const jsonLd = [
    {
      ...article,
      author: { '@type': 'Person', name: 'Robert Grzesik' },
      datePublished: '2025-02-05',
      dateModified: '2025-02-05',
      image: ogImageUrlForPath(STATIC_BLOG_PATH),
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': seo.canonical,
      },
    },
    ...otherStructuredData,
  ];

  return (
    <div className="blog-post ai-trading-bot">
      <SiteRouteHelmet path={STATIC_BLOG_PATH} jsonLd={jsonLd} />
      {/* Add sticky signup component with compelling title */}
      <BlogStickySignup title="Try AI Trading Bot Builder" />
      
      {/* Title */}
      <h1>Using AI to Make a Profitable Swing Trading Bot in Seconds</h1>

      {/* Author and Date */}
      <div className="blog-author-container">
        <div className="blog-author-info">
          <strong>Robert Grzesik</strong>
          <span className="blog-date">February 5, 2025</span>
          <div className="blog-reading-time">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
            <span className='blog-reading-time-text'>12 min read</span>
          </div>
        </div>
      </div>

      <p>
        <img
          className="headline-image"
          src="https://e835in5wmor.exactdn.com/wp-content/uploads/2025/02/Using-AI-to-Make-a-Profitable-Swing-Trading-Bot-in-Seconds.jpg?strip=all&lossy=1&ssl=1"
          alt="Using AI To Make a Profitable Swing Trading Bot in Seconds"
          style={{maxHeight: '350px', objectFit: 'contain'}} 
        />
      </p>

      <div className="blog-highlights-box">
        <h3>What You'll Learn</h3>
        <ul>
          <li>How to create a profitable trading bot in minutes with no coding</li>
          <li>Step-by-step process for building and optimizing swing trading strategies</li>
          <li>Deploying your bot to run 24/7 in the cloud</li>
          <li>Taking your strategy live with a real brokerage account</li>
        </ul>
      </div>

      {/* Remove the grid container and just use a single column layout */}
      <div className="blog-main-content">
        {/* Add Table of Contents */}
        <BlogTableOfContents />
        
        {/* Add testimonial near the beginning to build trust */}
        <BlogTestimonial
          quote="I tried multiple AI platforms to build trading algorithms, but only this one produced strategies that actually performed well in live markets. I'm now running three different bots that are all consistently profitable!"
          author="Michael R."
          position="Retail Trader, 2 years experience"
        />
        
        {/* Wrap main content in "post-content" container */}
        <div className="post-content">
          {/* Introduction */}
          <h2 id="introduction">Introduction</h2>
          <p>
            Imagine creating a profitable trading bot at the same time it takes to brew your
            morning coffee—no advanced coding skills are required. AI has shattered the old
            barriers to algorithmic trading, but not all AI is built the same. While popular
            models like ChatGPT or DeepSeek can offer coding tips, specialized trading-focused
            AI generates exactly the code you need for a bot that's truly market-ready. In
            seconds, you'll have everything set to launch your strategy and chase those
            profits. Sounds intriguing?
          </p>
          <p>
            In this blog, we'll walk through how to develop, refine, and optimize a swing
            trading bot using AI—step by step. If you'd like to follow along, we also offer a
            free trial so you can see for yourself just how effortless bot-building can
            be.
          </p>

          {/* Add callout box to emphasize important point */}
          <BlogCalloutBox type="info" title="Why This Matters">
            <p>Traditional algorithmic trading required extensive programming knowledge, mathematical expertise, and months of development time. With AI-powered tools, you can now compete with professional traders and hedge funds without any of those barriers.</p>
          </BlogCalloutBox>

          {/* Topics We'll Explore */}
          <h2 id="topics-we'll-explore-in-this-article">Topics We'll Explore in This Article</h2>
          <ul>
            <li>
              <strong>Zero-Code Trading Automation</strong> – See how AI sidesteps the need for
              manual coding.
            </li>
            <li>
              <strong>Building a Swing Trading Bot Step-by-Step</strong> – Follow our exact
              process to create a strategy and generate code in seconds.
            </li>
            <li>
              <strong>Refining Your Strategy for Maximum Profit</strong> – Learn how to tweak
              indicators and parameters to improve performance.
            </li>
            <li>
              <strong>Seamless Deployment with Replit</strong> – Discover how to test and run
              your strategy instantly in the cloud.
            </li>
            <li>
              <strong>Taking It Live on Alpaca</strong> – Get your bot trading in real markets,
              fully automated.
            </li>
            <li>
              <strong>Next Steps &amp; Additional Resources</strong> – Explore advanced
              features, training options, and community support to level up your skills.
            </li>
          </ul>

          {/* Main Section */}
          <h2 id="how-to-build-a-profitable-swing-trading-bot-with-ai-a-step-by-step-guide">
            How to Build a Profitable Swing Trading Bot with AI: A Step-by-Step Guide
          </h2>
          <p>
            With AI, the process of generating your trading strategy is incredibly simple and
            user-friendly, making it accessible to traders of all skill levels. Let's dive
            into a step-by-step guide on how to create a profitable swing trading bot.
          </p>

          <h3 id="step-1-sign-up-for-a-free-trial">Step 1: Sign Up for a Free Trial</h3>
          <p>
            Start by signing up for a free trial at{" "}
            <a href="https://botspot.trade" target="_blank" rel="noopener noreferrer">
              botspot.trade
            </a>
            . Once on the homepage, click on the "Create Strategy Using AI" button, and you'll
            be redirected to a page displaying the Starter Plan, which offers a free
            trial.
          </p>
          {/* Update first CTA button to open in new tab */}
          <div className="blog-cta-container">
            <a 
              className="blog-cta-button" 
              href="/sales" 
              target="_blank"
              rel="noopener noreferrer"
            >
              START YOUR FREE TRIAL NOW!
            </a>
          </div>
          
          <p>
            <img
              src="/blog/blog_2b3c4d5e.jpg"
              alt="BotSpot, AI Trading Strategy Generator"
              style={{maxHeight: '350px', objectFit: 'contain'}} 
            />
          </p>
          <p>
            In order to start your free trial, you will have to fill in some basic details
            like your email address and your card information, as well as your country. Upon
            completion, click on the Start Trial button, and your account will be activated
            instantly.
          </p>
          <p>
            <img
              src="/blog/blog_3c4d5e6f.jpg"
              alt="Signing Up with BotSpot"
              style={{maxHeight: '350px', objectFit: 'contain'}} 
            />
          </p>

          <h3 id="step-2-generate-trading-strategy-using-ai-bot-builder">Step 2: Generate Trading Strategy Using AI Bot Builder</h3>
          <p>
            Now, you have to generate the strategy using AI Bot Builder with your desired
            prompt. To create a strategy, simply enter your prompt into the <em>Describe Your Strategy
            Idea Here</em> field and click on the <em>Generate Strategy</em> button. For instance, we
            have entered the prompt: "I want a bot that trades TQQQ using RSI. If RSI &lt; 30
            then buy, if RSI &gt; 70 then sell."
          </p>
          <p>
            <img
              src="/blog/blog_4d5e6f70.jpg"
              alt="BotSpot's AI Bot Builder"
              style={{maxHeight: '350px', objectFit: 'contain'}} 
            />
          </p>
          <p>
            Once the strategy is generated, you will see a detailed output containing the
            required code for your trading bot. This output includes a well-structured Python
            script tailored to your specified strategy, ensuring that it is ready for
            implementation.
          </p>
          <p>
            <img
              src="/blog/blog_5e6f7081.jpg"
              alt="AI Trading Strategy Generation with BotSpot"
              style={{maxHeight: '350px', objectFit: 'contain'}} 
            />
          </p>

          <h3 id="step-3-sign-in-or-register-with-replit">Step 3: Sign In or Register with Replit</h3>
          <p>
            To deploy your strategy, you'll need to log in to or create a Replit account by
            visiting this link:{" "}
            <a href="https://replit.com" target="_blank" rel="noopener noreferrer">
              replit.com
            </a>
            . Replit will serve as the platform for running your trading bot, so this step is
            essential for smooth integration.
          </p>
          <p>
            <strong>Note:</strong> Alternatively, you can use VS Code to run your strategy.
            Since Replit is a cloud-based platform, you can run your strategy directly without
            needing to install or configure anything on your computer, while VS Code requires
            additional steps like installing dependencies and configuring your environment.
          </p>
          <p>
            <img
              src="/blog/blog_6f708192.jpg"
              alt="Replit Account Login"
              style={{maxHeight: '350px', objectFit: 'contain'}} 
            />
          </p>

          <h3 id="step-4-set-up-a-new-project-on-replit-and-paste-your-code">Step 4: Set Up a New Project on Replit and Paste Your Code</h3>
          <p>
            After signing into Replit, select "Python" from the template section, give your
            project a title, and click on the "Create an App" button to set up a new project.
            This will allow you to generate and deploy your trading bot using the strategy you
            created on BotSpot.
          </p>
          <p>
            <strong>Note:</strong> You can also choose your preferred privacy mode, allowing you to control who
            has access to your project.
          </p>
          <p>
            <img
              src="/blog/blog_7a8b9c0d.jpg"
              alt="New Python App Creation with Replit"
              style={{maxHeight: '350px', objectFit: 'contain'}} 
            />
          </p>
          <p>
            Once your app is created, copy the generated Python code from BotSpot and paste it
            into the Replit project under "main.py".
          </p>
          <p>
            <img
              src="/blog/blog_8b9c0d1e.jpg"
              alt="Code Placement in Replit"
              style={{maxHeight: '350px', objectFit: 'contain'}} 
            />
          </p>
          <p>
            This is an example of how your Replit project will look once you have copied and
            pasted your code from BotSpot.
          </p>
          <p>
            <img
              src="/blog/blog_9c0d1e2f.jpg"
              alt="Preview of Code in Replit"
              style={{maxHeight: '350px', objectFit: 'contain'}} 
            />
          </p>

          <h3 id="step-5-run-backtesting">Step 5: Run Backtesting</h3>
          <p>
            To backtest your strategy in Replit, click on "All Tools" on the left sidebar and
            select "Secrets" to configure your environment.
          </p>
          <p>
            <strong>Note:</strong> If you prefer to backtest in VS Code, you'll need to create
            a <code>.env</code> file.
          </p>
          <p>
            <img
              src="/blog/blog_0a1b2c3d.jpg"
              alt="Backtesting With Replit"
              style={{maxHeight: '350px', objectFit: 'contain'}} 
            />
          </p>
          <p>
            To initiate the backtesting, enter the required secrets, set <code>IS_BACKTESTING</code>{" "}
            to <code>TRUE</code> in the respective Key and Value spaces, and click on the "Run"
            button on the top.
          </p>
          <p>
            <img
              src="/blog/blog_1b2c3d4e.jpg"
              alt="Additional of Necessary Backtesting Values in Replit"
              style={{maxHeight: '350px', objectFit: 'contain'}} 
            />
          </p>
          <p>
            <strong>Note:</strong> You can also define the start and end dates if you like to
            fetch the results for custom data by adding a "New Secret." The Key should be{" "}
            <code>BACKTESTING_START</code> and <code>BACKTESTING_END</code> with the format of
            the Value being YYYY-MM-DD.
          </p>
          {/* Update second CTA button to open in new tab */}
          <div className="blog-cta-container">
            <SparkleButton 
              className="blog-cta-button primary-action large" 
              href="/sales" 
              target="_blank"
              rel="noopener noreferrer"
            >
              Claim Your Free Trial Today
            </SparkleButton>
          </div>

          <h3 id="step-6-view-backtesting-output">Step 6: View Backtesting Output</h3>
          <p>
            In order to view your backtesting output, go to "All Files" in the top left corner
            and look for files ending in <code>tearsheet.html</code> and <code>trade.html</code>.
            Click on both to download your backtesting output and view them. If these files
            don't appear right away, try refreshing the page.
          </p>
          <p>
            <img
              src="/blog/blog_2c3d4e5f.jpg"
              alt="Downloading Backtesting Files in Replit"
              style={{maxHeight: '350px', objectFit: 'contain'}} 
            />
          </p>
          <p>
            Here's the backtesting output:
          </p>
          <p>
            <img
              src="/blog/blog_3d4e5f60.jpg"
              alt="Tearsheet Backtesting Result for Original Prompt"
              style={{maxHeight: '350px', objectFit: 'contain'}} 
            />
          </p>
          <p>
            <img
              src="/blog/blog_4e5f6071.jpg"
              alt="Trader Backtesting Result for Original Prompt"
              style={{maxHeight: '350px', objectFit: 'contain'}} 
            />
          </p>
          <p>
            Now, if the strategy is not to your liking, Botspot also allows you to refine it
            by adjusting parameters, modifying indicators, or tweaking entry and exit
            conditions, and you can improve your strategy's performance.
          </p>

          <h3 id="step-7-refine-strategy-using-ai-bot-builder">Step 7: Refine Strategy Using AI Bot Builder</h3>
          <p>
            To refine your strategy, head to the AI Bot Builder and enter a new prompt in the
            "Refine Strategy" block.
          </p>
          <p>
            <img
              src="/blog/blog_5e6f7081.jpg"
              alt="Strategy Refinement in BotSpot"
              style={{maxHeight: '350px', objectFit: 'contain'}} 
            />
          </p>
          <p>
            For instance, the original prompt that was used is: "I want a bot that trades TQQQ
            using RSI. If RSI &lt; 30, then buy, if RSI &gt; 70 then sell", and now we want
            to refine it to a moving average strategy where the prompt will be: "Instead of
            RSI let's instead use a 200-day moving average as a momentum indicator to
            determine whether we own TQQQ or not. If we are below the 200-day moving average,
            sell. If we are above the 200-day moving average, buy." Next, click on "Generate
            Strategy" to refine your strategy.
          </p>
          <p>
            <img
              src="/blog/blog_6a7b8c9d.jpg"
              alt="Code Generation for Refined Strategy"
              style={{maxHeight: '350px', objectFit: 'contain'}} 
            />
          </p>

          <h3 id="step-8-copy-the-code-to-replit">Step 8: Copy the Code to Replit</h3>
          <p>
            Replace your existing code with your refined strategy in the "main.py" file.
          </p>
          <p>
            <img
              src="/blog/blog_7b8c9d0e.jpg"
              alt="Refined Code Placement in Replit"
              style={{maxHeight: '350px', objectFit: 'contain'}} 
            />
          </p>

          <h3 id="step-9-run-backtesting-to-generate-output">Step 9: Run Backtesting to Generate Output</h3>
          <p>
            To run the backtesting, input the necessary secrets in the "Secrets" option under
            "All Tools" like done in the earlier steps, set <code>IS_BACKTESTING</code> to{" "}
            <code>TRUE</code>, and specify the start and end dates in the format YYYY-MM-DD if
            you would like to. Click on the "Run" button on the top and generate the output.
          </p>
          <p>
            <img
              src="/blog/blog_8c9d0e1f.jpg"
              alt="Setting Backtesting Values for Refined Strategy in Replit"
              style={{maxHeight: '350px', objectFit: 'contain'}} 
            />
          </p>
          <p>
            Backtesting Output:
          </p>
          <p>
            With the refined code, the backtest is showing a very profitable outcome
            (woohoo!), as shown in the image below.
          </p>
          <p>
            <img
              src="/blog/blog_9d0e1f20.jpg"
              alt="Tearsheet Backtesting Result for Refined Prompt"
              style={{maxHeight: '350px', objectFit: 'contain'}} 
            />
          </p>
          <p>
            <img
              src="/blog/blog_0b1c2d3e.jpg"
              alt="Trader Backtesting Result for Refined Prompt"
              style={{maxHeight: '350px', objectFit: 'contain'}} 
            />
          </p>
          <p>
            Once you have achieved the desired results, the next step is to take it live by
            integrating it with a broker like Alpaca, which will allow for the seamless
            execution of trades in real-time.
          </p>
          {/* Update backtest CTA button to open in new tab */}
          <div className="blog-cta-container">
            <a 
              className="blog-cta-button" 
              href="/sales" 
              target="_blank"
              rel="noopener noreferrer"
            >
              START YOUR FREE TRIAL NOW!
            </a>
          </div>

          <h2 id="deploying-your-strategy-live-in-alpaca">Deploying Your Strategy Live in Alpaca</h2>

          <h3 id="step-1-set-up-your-alpaca-account">Step 1: Set Up Your Alpaca Account</h3>
          <p>
            To run the strategy live with Alpaca, first visit this link:{" "}
            <a
              href="https://alpaca.markets/"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://alpaca.markets/
            </a>{" "}
            and open an Alpaca account to access their trading API. You can easily sign up for
            free with Alpaca.
          </p>
          <p>
            <img
              src="/blog/blog_1c2d3e4f.jpg"
              alt="Alpaca Interface"
              style={{maxHeight: '350px', objectFit: 'contain'}} 
            />
          </p>
          <p>
            Once you click on the "Sign up for Free" button, you will be redirected to a page
            where you need to fill in some basic details like your name, country, and email
            address and set a password for your account.
          </p>
          <p>
            <img
              src="/blog/blog_2d3e4f50.jpg"
              alt="Alpaca Account Set Up"
              style={{maxHeight: '350px', objectFit: 'contain'}} 
            />
          </p>
          <p>
            After you have set up your account, you need to log in using your credentials and
            go to the homepage of Alpaca where you will have to complete MFA authentication
            using Microsoft Authenticator. Simply download the "Microsoft Authenticator" app on
            your mobile.
          </p>
          <p>
            <img
              src="/blog/blog_3e4f5061.jpg"
              alt="Alpaca Account Activation"
              style={{maxHeight: '350px', objectFit: 'contain'}} 
            />
          </p>
          <p>
            Go to "Home" and scroll down to navigate to the API settings, where you can find
            your API keys and API secret.
          </p>
          <p>
            <img
              src="/blog/blog_4f506172.jpg"
              alt="Fetching API Keys in Alpaca"
              style={{maxHeight: '350px', objectFit: 'contain'}} 
            />
          </p>

          <h3 id="step-2-copy-api-keys-to-replit">Step 2: Copy API Keys to Replit</h3>
          <p>
            The next step is to copy your API keys to Replit, where you'll be running your
            trading bot. In your secrets settings, ensure that <code>IS_BACKTESTING</code> is
            set to <code>FALSE</code> to enable live trading. Click on "New Secret" and add
            separate keys, "ALPACA_API_KEY" and "ALPACA_API_SECRET" in the Keys and paste the
            distinct code in Value.
          </p>
          <p>
            <img
              src="/blog/blog_5a607182.jpg"
              alt="Copy API Keys to Alpaca"
              style={{maxHeight: '350px', objectFit: 'contain'}} 
            />
          </p>

          <h3 id="step-3-create-a-trading-bot-with-botspot">Step 3: Create a Trading Bot with BotSpot</h3>
          <p>
            Next, you have to add a bot using your BotSpot account to get the API key for
            LumiWealth. Once you have logged in, go to the menu and click on the "Add Bot"
            button. You will be redirected to the "Create Your Trading Bot" page, where you
            have to give your bot a name, select your trading mode, choose your preferred
            privacy, and finally, click on the "Create Bot" button.
          </p>
          <p>
            <strong>Note:</strong> You can also add a description for your bot in the "Bot
            Description" space if you would like.
          </p>
          <p>
            <img
              src="/blog/blog_6b7c8d9e.jpg"
              alt="BotSpot's Add Bot Page"
              style={{maxHeight: '350px', objectFit: 'contain'}} 
            />
          </p>

          <h3 id="step-4-run-your-strategy-live-in-replit">Step 4: Run Your Strategy Live in Replit</h3>
          <p>
            Once your bot is created, copy the API key for LumiWealth into Replit under the
            variable <code>LUMIWEALTH_API_KEY</code>.
          </p>
          <p>
            <img
              src="/blog/blog_7c8d9e0f.jpg"
              alt="Copying Lumiwealth API Keys to Replit"
              style={{maxHeight: '350px', objectFit: 'contain'}} 
            />
          </p>
          <p>
            After setting everything up, click on the "Run" button at the top.
          </p>
          <p>
            To deploy your project, start by clicking your project name in the top left, then
            select "Deploy your project." Choose "Reserved VM" and click "Set up your
            deployment." Next, select your CPU and memory configuration (typically, 0.25 vCPU
            and 1GB RAM is sufficient) and proceed to purchase and deploy.
          </p>
          <p>
            When prompted, choose "Background worker" as the app type. This step is crucial for
            ensuring proper execution. Then, click "Deploy." Once the deployment process
            begins, wait a few minutes for it to launch, then check your logs to confirm that
            everything is running smoothly.
          </p>
          <p>
            <img
              src="/blog/blog_0c9d0e1f.jpg"
              alt="Deploying In Replit 1"
              style={{maxHeight: '350px', objectFit: 'contain'}} 
            />
          </p>
          <p>
            <img
              src="/blog/blog_840e1f20.jpg"
              alt="Deploying In Replit 2"
              style={{maxHeight: '350px', objectFit: 'contain'}} 
            />
          </p>
          <p>
            <img
              src="/blog/blog_011c2d3e.jpg"
              alt="Deploying In Replit 3"
              style={{maxHeight: '350px', objectFit: 'contain'}} 
            />
          </p>
          <p>
            Finally, monitor your bot's performance on BotSpot, where you can track trades,
            make adjustments, and optimize your strategy for better results. The strategy
            detailed above is actively running live, and if you're interested, you can follow
            along by clicking here to observe its performance firsthand. For more detailed
            information on deploying your strategy, you can visit this link: Deployment Guide.
          </p>

          <h2 id="result-of-live-trading-in-the-market-using-alpaca-as-a-broker">Result of Live Trading in The Market Using Alpaca as a Broker</h2>
          <p>
            <img
              src="/blog/blog_gt5d3e4f.jpg"
              alt="Result of Live Trading in Market 1"
              style={{maxHeight: '350px', objectFit: 'contain'}} 
            />
          </p>
          <p>
            <img
              src="/blog/blog_lk7e4f50.jpg"
              alt="Result of Live Trading in Market 2"
              style={{maxHeight: '350px', objectFit: 'contain'}} 
            />
          </p>

          {/* Final CTA inserted before Bonus Section */}
          <div className="blog-cta-container">
            <SparkleButton 
              className="blog-cta-button primary-action large" 
              href="/sales" 
              target="_blank"
              rel="noopener noreferrer"
            >
              Get Started with Your Free Trial
            </SparkleButton>
          </div>
        </div>
      </div>
      {/* TODO: Add this back after we figure out Odoo integration */}
      {/* <BlogBonusSection /> */}
    </div>
  );
}
